import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Appstore } from "../../../redux";
import FilteredTableCore from "../../../components/Organismos/Table/FilteredTableCore";
import {
  buildAuditLogColumnsDef,
  buildAuditLogFiltersData,
  formatAuditLogExcelRows,
  formatAuditLogRows,
} from "../Logs/auditLogTableConfig";

export type ProfessionalUserLogsProps = {
  /** Author mode: changes this user made. */
  userId?: string;
  firmId?: string;
  /**
   * Subject mode: changes made to this `Person.id`, by anyone. Takes precedence over
   * `userId` / `firmId`.
   */
  entityId?: string;
  /** When rendered inside a Modal (nested scroll). */
  embeddedInScrollContainer?: boolean;
};

const ProfessionalUserLogs = ({
  userId: userIdProp,
  firmId: firmIdProp,
  entityId,
  embeddedInScrollContainer = false,
}: ProfessionalUserLogsProps = {}) => {
  const { id: firmIdParam, userId: userIdParam } = useParams<{
    id?: string;
    userId?: string;
  }>();
  const selectedFirmId = useSelector(
    (state: Appstore) => state.manageFirm.selectedFirm?.id,
  );
  const userId = userIdProp ?? userIdParam;
  const firmId = firmIdProp ?? firmIdParam ?? selectedFirmId;

  const persistentFilter = useMemo(() => {
    const filter: Record<string, { value: string; comparisonType: string }> =
      {};
    if (entityId) {
      filter.entity = { value: "Person", comparisonType: "EQUAL" };
      filter.entityId = { value: entityId, comparisonType: "EQUAL" };
      return filter;
    }
    if (userId) {
      filter.userId = { value: userId, comparisonType: "EQUAL" };
    }
    if (firmId) {
      filter.firmId = { value: firmId, comparisonType: "EQUAL" };
    }
    return filter;
  }, [entityId, userId, firmId]);

  const filtersData = useMemo(
    () => buildAuditLogFiltersData(embeddedInScrollContainer),
    [embeddedInScrollContainer],
  );

  const columnsDef = useMemo(() => buildAuditLogColumnsDef(), []);

  return (
    <div
      className={
        embeddedInScrollContainer ? "flex flex-col min-w-0" : "flex flex-col gap-5"
      }
    >
      {!entityId && !userId ? (
        <p className="text-sm text-gray-font">
          Select a professional to review their audit log.
        </p>
      ) : (
        <FilteredTableCore
          key={entityId ?? userId}
          columnsDef={columnsDef}
          endpoint="/auditLog"
          filtersData={filtersData}
          persistentFilter={persistentFilter}
          defaultSorting={{ field: "timestamp", order: "desc" }}
          searchPlaceholder="Search by Action, Entity or Changes"
          showSearchInput
          persistFilters={false}
          formatExcelData={formatAuditLogExcelRows}
          formatData={formatAuditLogRows}
          excelFilename="professional-audit-log.xlsx"
          emptyStateMessage="No audit records for the selected date range."
          embeddedInScrollContainer={embeddedInScrollContainer}
          containerclassName="!px-10 !pt-4"
        />
      )}
    </div>
  );
};

export default ProfessionalUserLogs;
